// Full-page session detail: header, status actions, and tabbed content
import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, QrCode, RotateCcw, AlertCircle } from 'lucide-react'

import { Button } from '@/ui/button'
import { Card, CardContent } from '@/ui/card'
import { Modal } from '@/ui/modal'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/ui/tabs'
import { useClassSessionStore, useSessionStore, getCoursePassingScore } from '@/features/classes/stores'
import type { SessionStatus, OpenCheckinPayload } from '@/features/classes/stores'
import { useToast } from '@/core/hooks/useToast'
import { ConfirmDialog } from '@/core/components/ConfirmDialog'

import { fmtDate } from '../utils/formatters'

import { QRDisplay } from './QRDisplay'
import { SessionStatusBadge } from './StatusBadges'
import { RealtimeDashboard } from './RealtimeDashboard'
import { StudentJoinModal } from './StudentJoinModal'
import { SessionActions } from './SessionActions'
import { SessionOverview } from './SessionOverview'
import { SessionAttendeesTab } from './SessionAttendeesTab'
import { SessionSettingsTab } from './SessionSettingsTab'
import { EditSessionModal } from './EditSessionModal'
import { CertificatesTab } from './CertificatesTab'
import { EndClassModal } from './EndClassModal'
import { OpenCheckinModal } from './OpenCheckinModal'

interface SessionDetailPageProps {
  sessionId: string
}

export function SessionDetailPage({ sessionId }: SessionDetailPageProps) {
  const navigate = useNavigate()
  const { show, ToastUI } = useToast()
  const sessions = useClassSessionStore((s) => s.sessions)
  const sessionsLoaded = useClassSessionStore((s) => s.sessionsLoaded)
  const loadSessions = useClassSessionStore((s) => s.loadSessions)
  const loadSessionAttendees = useClassSessionStore((s) => s.loadSessionAttendees)
  const loadCertificates = useClassSessionStore((s) => s.loadCertificates)
  const openCheckin = useClassSessionStore((s) => s.openCheckin)
  const beginClass = useClassSessionStore((s) => s.beginClass)
  const closeSession = useClassSessionStore((s) => s.closeSession)

  // Defensive reload: if a user lands directly on the detail page and the
  // global sync hasn't loaded sessions yet, trigger a load. The global sync in
  // DashboardLayout normally handles this, but this guard prevents "Session not
  // found" flashes on hard refreshes or navigation edge cases.
  useEffect(() => {
    if (!sessionsLoaded) {
      void loadSessions()
    }
  }, [sessionsLoaded, loadSessions])

  const attendeeCount = useClassSessionStore(
    (s) => s.attendees.filter((a) => a.sessionId === sessionId).length
  )

  const courses = useClassSessionStore((s) => s.courses)
  const session = sessions.find((s) => s.id === sessionId)
  const course = session ? courses.find((c) => c.slug === session.courseId) : undefined
  const passingScore = getCoursePassingScore(course)

  // Load the attendee roster for this session as soon as the session is known.
  // Attendees are required by the Overview (count) and Certificates tabs, but
  // the Attendees tab was the only place that fetched them, so visiting the
  // Certificates tab first showed "No certificates" until Attendees was opened.
  useEffect(() => {
    if (!session) {return}
    void loadSessionAttendees(sessionId)
  }, [session, sessionId, loadSessionAttendees])

  const [actionLoading, setActionLoading] = useState(false)
  const [joinOpen, setJoinOpen] = useState(false)
  const [qrOpen, setQrOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'attendees' | 'certificates' | 'settings'>('overview')
  const [endModalOpen, setEndModalOpen] = useState(false)
  const [confirmAction, setConfirmAction] = useState<'delete' | null>(null)
  const [checkinOpen, setCheckinOpen] = useState(false)

  function handleOpenCheckin() {
    setCheckinOpen(true)
  }

  async function executeOpenCheckin(payload: OpenCheckinPayload) {
    setActionLoading(true)
    try {
      await openCheckin(sessionId, payload)
    } finally {
      setActionLoading(false)
    }
  }

  async function handleBeginClass() {
    setActionLoading(true)
    try {
      await beginClass(sessionId)
    } catch {
      show('Failed to begin class. Please try again.', 'error')
    } finally {
      setActionLoading(false)
    }
  }

  function handleEnd() {
    setEndModalOpen(true)
  }

  async function executeEnd(attendeeIds: string[]) {
    setEndModalOpen(false)
    setActionLoading(true)
    try {
      const result = await closeSession(sessionId, attendeeIds)
      await loadCertificates()
      show(
        `Class ended. ${result.certificatesCreated} certificate(s) created and ${result.certificatesEmailed} email(s) queued.`,
        'success'
      )
    } catch {
      show('Failed to end session. Please try again.', 'error')
    } finally {
      setActionLoading(false)
    }
  }

  function handleDeleteSession() {
    setConfirmAction('delete')
  }

  async function executeDelete() {
    setConfirmAction(null)
    setActionLoading(true)
    try {
      await useSessionStore.getState().deleteSession(sessionId)
      navigate({ to: '/classes' })
    } catch (err) {
      setActionLoading(false)
      show(err instanceof Error ? err.message : 'Failed to cancel class', 'error')
    }
  }

  useEffect(() => {
    if (!session) {return}
    const validTabs: Record<SessionStatus, Array<'overview' | 'attendees' | 'certificates' | 'settings'>> = {
      draft: ['overview', 'attendees', 'settings'],
      checking: ['overview', 'attendees'],
      active: ['overview', 'attendees'],
      closed: ['overview', 'attendees', 'certificates'],
    }
    if (!validTabs[session.status].includes(activeTab)) {
      setActiveTab('overview')
    }
  }, [session, activeTab])

  if (!sessionsLoaded) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <RotateCcw className="w-8 h-8 text-slate-150 animate-spin" />
        <p className="text-slate-400 text-sm">Loading session…</p>
      </div>
    )
  }

  if (!session) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => navigate({ to: '/classes' })}>
          <ArrowLeft className="w-4 h-4" />
          Back to Classes
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-red-500 font-semibold">Session not found</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/classes' })}>
            <ArrowLeft className="w-4 h-4" />
            Classes
          </Button>
          <div>
            <h1 className="text-xl font-bold text-navy flex items-center gap-2">
              {course?.name ?? session.courseId}
              <SessionStatusBadge status={session.status} />
            </h1>
            <p className="text-sm text-slate-500">
              📍 {session.locationName ?? session.location ?? '—'} · 📅 {fmtDate(session.date)} · 👤 {session.instructorName}
            </p>
          </div>
        </div>
        <SessionActions
          session={session}
          loading={actionLoading}
          onOpenCheckin={handleOpenCheckin}
          onBeginClass={handleBeginClass}
          onEndClass={handleEnd}
          onShowQR={() => setQrOpen(true)}
          onAddStudent={() => setJoinOpen(true)}
          onEdit={() => setEditOpen(true)}
          onCancelClass={handleDeleteSession}
        />
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as 'overview' | 'attendees' | 'certificates' | 'settings')}
      >
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="attendees">Attendees</TabsTrigger>
          {session.status === 'closed' && <TabsTrigger value="certificates">Certificates</TabsTrigger>}
          {session.status === 'draft' && <TabsTrigger value="settings">Settings</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <SessionOverview session={session} attendeeCount={attendeeCount} />
          {(session.status === 'checking' || session.status === 'active') && <RealtimeDashboard session={session} />}
        </TabsContent>

        <TabsContent value="attendees">
          <SessionAttendeesTab sessionId={sessionId} passingScore={passingScore} />
        </TabsContent>

        <TabsContent value="certificates">
          <CertificatesTab sessionId={sessionId} />
        </TabsContent>

        <TabsContent value="settings">
          <SessionSettingsTab
            onEdit={() => setEditOpen(true)}
            onCancel={handleDeleteSession}
            loading={actionLoading}
          />
        </TabsContent>
      </Tabs>

      {qrOpen && (
        <Modal open={qrOpen} onClose={() => setQrOpen(false)} showCloseButton className="max-w-md">
          <h3 className="text-base font-semibold text-navy mb-3 flex items-center gap-2">
            <QrCode className="w-4 h-4 text-primary" />
            Registration QR
          </h3>
          <QRDisplay token={session.qrToken} />
          <p className="text-xs text-slate-500 mt-3 text-center">
            Students scan this code to register for the class.
          </p>
        </Modal>
      )}

      {editOpen && <EditSessionModal session={session} onClose={() => setEditOpen(false)} />}
      {joinOpen && <StudentJoinModal sessionId={sessionId} onClose={() => setJoinOpen(false)} />}
      {checkinOpen && (
        <OpenCheckinModal
          session={session}
          onClose={() => setCheckinOpen(false)}
          onConfirm={executeOpenCheckin}
        />
      )}

      <EndClassModal
        sessionId={sessionId}
        open={endModalOpen}
        onClose={() => setEndModalOpen(false)}
        onEnd={executeEnd}
      />
      <ConfirmDialog
        open={confirmAction === 'delete'}
        title="Cancel Class"
        description="Are you sure you want to cancel this class? This cannot be undone."
        confirmText="Cancel Class"
        variant="destructive"
        onConfirm={executeDelete}
        onCancel={() => setConfirmAction(null)}
      />

      {ToastUI}
    </div>
  )
}
